import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Avatar, Box, Button, Chip, Divider, Drawer, Fab, IconButton, LinearProgress, Paper,
  Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import PsychologyRoundedIcon from '@mui/icons-material/PsychologyRounded';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import AttachFileRoundedIcon from '@mui/icons-material/AttachFileRounded';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { FC, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import useUser from '../../../../hooks/useUser';
import { AnalystAction, AskAnswer, ChatTurn, GargiFile } from '../../interfaces/models';
import { askGargiStream, AskProgress, downloadGargiFile, getSuggestions, interjectGargi, saveConversation, uploadGargiFile } from '../../services/models';
import ChatHistory, { HISTORY_KEY } from './ChatHistory';
import { GargiOpportunity } from '../../interfaces/gargi';
import { ActDialog, OpportunityButtons, Pending } from '../Gargi/OpportunityActions';
import AudienceCard from './AudienceCard';
import JourneyCard from './JourneyCard';
import { MemoryPanel, useGargiNotes } from '../Memory/GargiMemory';
import { GargiPage, gargiPage } from '../../../../shared/gargi/pages';
import { useGargiAi } from '../../../../shared/gargi/access';
import JourneyModelPicker from '../../../GargiJourneyAssistant/components/JourneyChatPanel/JourneyModelPicker';
import { formatInr } from '../../utils/format';
import { count, humanize } from '../Models/common';
import AnalystChartView from './AnalystChartView';
import { GargiContext, GargiContextValue } from './context';
import MarkdownText from './MarkdownText';

export { useGargi } from './context';

const FALLBACK_QUESTIONS = [
  'Who should we target in the next two weeks, and with what?',
  'Which customers are due to buy now by their own rhythm?',
  'What should we cross-sell to last month\'s buyers?',
  'How did this month compare with the same month last year, by store?',
];

// v3: everyone starts on the default (Haiku 5.5, the cheapest) again; a choice made after that is remembered.
const MODEL_KEY = 'gargi:ask-model:v3';
const newConversationId = () => `c-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

const PRIORITY_COLOUR = { high: 'error', medium: 'warning', low: 'default' } as const;

const brief = (a: AnalystAction) => [
  a.title,
  `Audience: ${a.audience}${a.audience_size ? ` (${count(a.audience_size)} customers)` : ''}`,
  `Offer: ${a.offer}`,
  `Channel: ${a.channel} · Timing: ${a.timing}`,
  a.message ? `Message: ${a.message}` : '',
  a.revenue_at_stake ? `Revenue at stake: ${formatInr(a.revenue_at_stake)} — ${a.assumption}` : `Assumption: ${a.assumption}`,
].filter(Boolean).join('\n');

// The action as an opportunity the shared actions understand; counted live when an action opens.
const asOpportunity = (action: AnalystAction): GargiOpportunity | null =>
  action.template_id
    ? {
      id: action.template_id,
      name: action.title,
      description: action.audience,
      customerCount: null,
      predictedRevenue: action.revenue_at_stake ? Math.round(action.revenue_at_stake) : null,
      atv: null,
      metadata: {},
      // The card's campaign: Create journey builds these messages, waits and follow-ups with approved templates.
      campaign: { offer: action.offer, channel: action.channel, timing: action.timing },
    }
    : null;

// A recommended play from Gargi. A campaign on a growth play's customers opens straight into the full journey;
// a campaign on any other group is designed with Gargi (it defines the audience first); operations work is a brief.
const ActionCard: FC<{ action: AnalystAction; onAsk?: (q: string) => void }> = ({ action, onAsk }) => {
  const [copied, setCopied] = useState(false);
  const [pending, setPending] = useState<Pending>(null);
  const operations = action.kind === 'operations';
  const opportunity = operations ? null : asOpportunity(action);
  const copy = () => {
    navigator.clipboard?.writeText(brief(action)).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <Paper variant='outlined' sx={{ p: 1.75, borderRadius: 2.5, borderColor: 'primary.main' }}>
      <Stack direction='row' spacing={1} alignItems='flex-start' justifyContent='space-between'>
        <Typography variant='subtitle2' fontWeight={700}>{action.title}</Typography>
        {operations && <Chip size='small' label='Operations task' variant='outlined' sx={{ height: 20 }} />}
        <Chip size='small' label={action.priority} color={PRIORITY_COLOUR[action.priority]} variant='outlined' sx={{ height: 20, textTransform: 'capitalize' }} />
      </Stack>
      <Box sx={{ display: 'grid', gridTemplateColumns: '88px 1fr', rowGap: 0.5, columnGap: 1, mt: 1, fontSize: 13 }}>
        <Typography variant='caption' color='text.secondary'>Audience</Typography>
        <Typography variant='body2'>{action.audience}{action.audience_size ? <strong>{` · ${count(action.audience_size)}`}</strong> : null}</Typography>
        <Typography variant='caption' color='text.secondary'>Offer</Typography>
        <Typography variant='body2'>{action.offer}</Typography>
        <Typography variant='caption' color='text.secondary'>Channel</Typography>
        <Typography variant='body2'>{action.channel} · {action.timing}</Typography>
        {action.revenue_at_stake ? (
          <>
            <Typography variant='caption' color='text.secondary'>At stake</Typography>
            <Typography variant='body2'><strong>{formatInr(action.revenue_at_stake)}</strong></Typography>
          </>
        ) : null}
      </Box>
      <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mt: 0.75 }}>{action.assumption}</Typography>
      {action.message && !operations && (
        <Typography variant='body2' sx={{ mt: 0.75, p: 1, borderRadius: 1.5, bgcolor: 'grey.50' }}>“{action.message}”</Typography>
      )}
      <Stack direction='row' spacing={1} sx={{ mt: 1.25 }} flexWrap='wrap' useFlexGap>
        {opportunity && <OpportunityButtons o={opportunity} onAct={setPending} />}
        {!operations && !opportunity && onAsk && (
          <Button size='small' variant='contained' disableElevation
            onClick={() => onAsk(`Design the journey for "${action.title}": define its audience (${action.audience}), then the messages with our approved templates, waits and follow-ups to those who don't buy, with a 10% control group.`)}>
            Design the journey with Gargi
          </Button>
        )}
        <Button size='small' startIcon={<ContentCopyRoundedIcon fontSize='small' />} onClick={copy}>{copied ? 'Copied' : 'Copy brief'}</Button>
      </Stack>
      <ActDialog pending={pending} onClose={() => setPending(null)} />
    </Paper>
  );
};

const SECTION_LABEL: Record<string, string> = {
  growth_plays: 'growth plays', validation: 'how past suggestions performed', who_will_buy: 'who will buy next',
  prospects: 'top prospects', personas: 'customer groups', rhythm: 'customers due by their own rhythm',
  stores_outlook: 'store outlook', forecast: 'sales forecast', store_scorecard: 'store scorecard, targets and forecasts',
  assortment: 'category and style performance', rebuy_cycles: 'rebuy cycles', omnichannel: 'store and online customers',
};

// A step in business terms: what Gargi read or worked out, never table names.
const describeStep = (step: { tool: string; input?: unknown }) => {
  const input = (step.input ?? {}) as Record<string, unknown>;
  if (step.tool === 'get_sections') {
    const names = ((input.sections as string[]) ?? []).map((n) => SECTION_LABEL[n] ?? humanize(n).toLowerCase());
    return names.length ? `Read ${names.join(', ')}` : 'Read this business’s customer and sales models';
  }
  if (step.tool === 'run_analysis') {
    if (!input.analysis) return 'Worked out the numbers from bills';
    const window = input.from && input.to ? ` for ${input.from} to ${input.to}` : '';
    return `Worked out ${humanize(String(input.analysis)).toLowerCase()}${window}`;
  }
  if (step.tool === 'heard') return `Read your note: “${String(input.text ?? '')}”`;
  if (step.tool === 'code_execution') return 'Ran an analysis in Python';
  if (step.tool === 'casa_knowledge') return `Looked up CASA's help on ${String(input.query ?? '')}`.trim();
  if (step.tool === 'remember') return input.title ? `Remembered “${input.title}”` : 'Saved a note for next time';
  if (step.tool === 'create_audience') return `Defined the audience “${String(input.name ?? 'customers')}”`;
  if (step.tool === 'query_data') return input.purpose ? `Worked out ${String(input.purpose).replace(/^./, (c) => c.toLowerCase())}` : 'Ran a custom query on bills';
  if (step.tool === 'drill_down') return `Looked into ${input.kind === 'store' ? 'store' : 'category'} ${String(input.name ?? '')}`.trim();
  if (step.tool === 'design_journey') return input.name ? `Designed the journey “${input.name}”` : 'Designed the journey';
  if (step.tool === 'render_chart') return input.title ? `Drew “${input.title}”` : 'Drew a chart';
  if (step.tool === 'propose_action') return input.title ? `Proposed “${input.title}”` : 'Wrote up an action';
  return step.tool;
};

const ACCEPT = '.csv,.tsv,.txt,.json,.xlsx,.xls,.pdf,.png,.jpg,.jpeg,.webp,.gif';
const fileSize = (bytes: number) => (bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`);

// Files Gargi wrote for this answer, each a download.
const AnswerFiles: FC<{ files: GargiFile[] }> = ({ files }) => {
  const [failed, setFailed] = useState('');
  return (
    <Stack spacing={0.75}>
      <Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap>
        {files.map((f) => (
          <Button key={f.id} size='small' variant='outlined' startIcon={<FileDownloadOutlinedIcon />}
            onClick={() => downloadGargiFile(f).catch(() => setFailed(f.name))} sx={{ textTransform: 'none' }}>
            {f.name}{f.size ? ` · ${fileSize(f.size)}` : ''}
          </Button>
        ))}
      </Stack>
      {failed && <Alert severity='warning'>{failed} could not be downloaded. Files are kept for 7 days.</Alert>}
    </Stack>
  );
};

// What Gargi saved for next time while answering, each with an undo.
const Remembered: FC<{ notes: NonNullable<AskAnswer['remembered']> }> = ({ notes }) => {
  const { remove, refresh } = useGargiNotes();
  const [undone, setUndone] = useState<string[]>([]);
  useEffect(() => { refresh(); }, []);  // eslint-disable-line react-hooks/exhaustive-deps
  const shown = notes.filter((n) => !undone.includes(n.id));
  if (!shown.length) return null;
  return (
    <Stack spacing={0.5}>
      {shown.map((n) => (
        <Stack key={n.id} direction='row' spacing={1} alignItems='center' sx={{ px: 1.25, py: 0.75, borderRadius: 2, bgcolor: 'grey.50' }}>
          <Typography variant='caption' color='text.secondary' sx={{ flex: 1 }}>
            <strong>Remembered{n.kind === 'idea' ? ' as an opportunity' : ''}:</strong> {n.title ? `${n.title} — ` : ''}{n.text}
          </Typography>
          <Button size='small' color='inherit' sx={{ minWidth: 0, color: 'text.secondary' }}
            onClick={() => remove.mutate(n.id, { onSuccess: () => setUndone((u) => [...u, n.id]) })}>Undo</Button>
        </Stack>
      ))}
    </Stack>
  );
};

const AnswerView: FC<{ answer: AskAnswer; question?: string; onAsk?: (q: string) => void }> = ({ answer, question, onAsk }) => (
  <Stack spacing={2}>
    <MarkdownText text={answer.answer} />
    {!!answer.files?.length && <AnswerFiles files={answer.files} />}
    {!!answer.remembered?.length && <Remembered notes={answer.remembered} />}
    {!!answer.audiences?.length && (
      <Stack spacing={1.25}>
        {answer.audiences.map((a) => (
          <AudienceCard key={a.id} audience={a} onJourney={onAsk && ((x) => onAsk(`Design a journey for the audience “${x.name}” (audience_id ${x.id}): pick the channel, message template, offer and timing that suit them.`))} />
        ))}
      </Stack>
    )}
    {answer.charts.map((chart, i) => (
      <Paper key={i} variant='outlined' sx={{ p: 1.75, borderRadius: 2.5 }}><AnalystChartView chart={chart} /></Paper>
    ))}
    {!!answer.journeys?.length && (
      <Stack spacing={1.25}>
        {/* Each design call is the whole journey, so the last one is the current version. */}
        <JourneyCard journey={answer.journeys[answer.journeys.length - 1]} question={question} />
      </Stack>
    )}
    {!!answer.actions.length && (
      <Stack spacing={1.25}>
        <Typography variant='overline' color='text.secondary'>Recommended plays</Typography>
        {answer.actions.map((action, i) => <ActionCard key={i} action={action} onAsk={onAsk} />)}
      </Stack>
    )}
    {!!answer.steps.length && (
      <Accordion disableGutters elevation={0} sx={{ bgcolor: 'transparent', '&:before': { display: 'none' } }}>
        <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />} sx={{ px: 0, minHeight: 32, '& .MuiAccordionSummary-content': { my: 0.5 } }}>
          <Typography variant='caption' color='text.secondary'>
            How Gargi worked this out · {answer.steps.length} steps{answer.seconds ? ` · ${Math.round(answer.seconds)}s` : ''}
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 0, pt: 0 }}>
          <Stack component='ol' spacing={0.5} sx={{ pl: 2.5, m: 0 }}>
            {answer.steps.map((step, i) => <Typography component='li' key={i} variant='caption' color='text.secondary'>{describeStep(step)}</Typography>)}
          </Stack>
          <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mt: 1 }}>
            Every number comes from this business&apos;s data through fixed, read-only analyses. Customer details are masked.
          </Typography>
        </AccordionDetails>
      </Accordion>
    )}
  </Stack>
);

const TOOL_LABEL: Record<string, string> = {
  get_sections: 'Reading your customer and sales models',
  run_analysis: 'Running the numbers',
  render_chart: 'Drawing a chart',
  propose_action: 'Writing up an action',
};

export interface LiveAnswer {
  text: string;
  steps: { tool: string; input: Record<string, unknown> }[];
}

// While Gargi works: each step in plain words (what it is reading or working out), with the exact request one
// click away for CASA staff checking the numbers.
const Thinking: FC<{ live: LiveAnswer }> = ({ live }) => {
  const [seconds, setSeconds] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(timer);
  }, []);
  const last = live.steps[live.steps.length - 1];
  const phase = last ? TOOL_LABEL[last.tool] ?? 'Working' : seconds < 8 ? 'Reading the question' : 'Looking at your customers and sales';
  return (
    <Box>
      {!!live.steps.length && (
        <Stack spacing={0.5} sx={{ mb: 1 }}>
          {live.steps.map((step, i) => (
            <Box key={i}>
              <Typography variant='body2'>✓ {describeStep(step)}</Typography>
              <Typography component='details' variant='caption' color='text.secondary' sx={{ ml: 2.5 }}>
                <summary style={{ cursor: 'pointer' }}>Details</summary>
                <Box component='pre' sx={{ m: 0, whiteSpace: 'pre-wrap', fontSize: 11 }}>{step.tool} {JSON.stringify(step.input, null, 1)}</Box>
              </Typography>
            </Box>
          ))}
        </Stack>
      )}
      {live.text && <Typography variant='body2' sx={{ whiteSpace: 'pre-wrap', mb: 1 }}>{live.text}</Typography>}
      <Typography variant='body2' color='text.secondary'>{phase}… {seconds}s</Typography>
      <LinearProgress sx={{ mt: 1, borderRadius: 1 }} />
    </Box>
  );
};

interface DrawerProps {
  open: boolean;
  onClose: () => void;
  pending: { question: string; context?: Record<string, unknown>; id: number } | null;
  page: GargiPage & { path: string };   // the screen the person is on: named in the header, sent with questions
}

const AskGargiDrawer: FC<DrawerProps> = ({ open, onClose, pending, page }) => {
  const queryClient = useQueryClient();
  // The chat, this person's earlier conversations, or what Gargi remembers about the business.
  const [panel, setPanel] = useState<'chat' | 'history' | 'memory'>('chat');
  const showHistory = panel === 'history';
  const pageContext = useMemo(() => ({ screen: page.screen, page: page.path }), [page.screen, page.path]);
  // The screen this conversation is about, so opening Gargi on another screen can offer a new chat.
  const [threadScreen, setThreadScreen] = useState<{ screen: string; name: string } | null>(null);
  const { selectedBusiness } = useUser();
  const [turns, setTurns] = useState<ChatTurn[]>([]);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [live, setLive] = useState<LiveAnswer>({ text: '', steps: [] });
  const lastPending = useRef<number | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const unavailableRef = useRef(false);
  const bottom = useRef<HTMLDivElement>(null);

  const suggestions = useQuery({
    queryKey: ['intelligence-suggestions', selectedBusiness?.id],
    queryFn: getSuggestions,
    enabled: open && !!selectedBusiness?.id,
    staleTime: 60 * 60 * 1000,
    retry: 0,
  });

  // A conversation belongs to one business.
  // What the conversation is about (the card or screen it started from) travels with every follow-up question.
  const contextRef = useRef<Record<string, unknown> | undefined>(undefined);
  // Files attached in this conversation go with every question, so follow-ups can use them too.
  const [attachments, setAttachments] = useState<GargiFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const fileInput = useRef<HTMLInputElement>(null);
  const attach = async (list: FileList | null) => {
    if (!list?.length) return;
    setUploading(true);
    setUploadError('');
    try {
      for (const file of Array.from(list)) {
        if (file.size > 20 * 1024 * 1024) throw Object.assign(new Error(), { message: `${file.name} is larger than 20 MB.` });
        const uploaded = await uploadGargiFile(file);
        setAttachments((current) => [...current, uploaded]);
      }
    } catch (error: any) {
      setUploadError(error?.response?.data?.message ?? error?.message ?? 'The file could not be attached.');
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  };
  // Names the conversation for the server, which keeps what Gargi already read so follow-ups build on it.
  const conversationRef = useRef(newConversationId());
  const restart = () => { setTurns([]); setAttachments([]); setUploadError(''); contextRef.current = undefined; conversationRef.current = newConversationId(); };
  useEffect(() => { setTurns([]); setAttachments([]); contextRef.current = undefined; conversationRef.current = newConversationId(); }, [selectedBusiness?.id]);
  // Follow new text only while the reader is at the bottom; scrolling up to read stays put.
  const scroller = useRef<HTMLDivElement>(null);
  const following = useRef(true);
  const onScroll = () => {
    const el = scroller.current;
    if (el) following.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  };
  useEffect(() => { following.current = true; bottom.current?.scrollIntoView({ behavior: 'smooth' }); }, [turns.length]);
  useEffect(() => { if (following.current) bottom.current?.scrollIntoView({ block: 'end' }); }, [live.text, live.steps.length]);

  const { refetch: refetchStatus } = suggestions;

  // The Claude model for this browser's questions: the same picker as the journey builder's Gargi AI, remembered
  // here; the server answers with its default for anything it doesn't offer.
  const [model, setModel] = useState<string>(() => {
    try { return localStorage.getItem(MODEL_KEY) ?? ''; } catch { return ''; }
  });
  const catalog = suggestions.data?.models;
  const pickModel = (id: string) => {
    setModel(id);
    try { localStorage.setItem(MODEL_KEY, id); } catch { /* storage blocked: the choice lasts this visit */ }
  };
  const activeModel = model || catalog?.defaultModelId || '';

  const send = useCallback(async (question: string, context?: Record<string, unknown>) => {
    const text = question.trim();
    if (!text || busy || unavailableRef.current) return;
    const history = turns;
    const sentFiles = attachments.filter((f) => !turns.some((turn) => turn.files?.some((x) => x.id === f.id)));
    setTurns((t) => [...t, { role: 'user', content: text, files: sentFiles.length ? sentFiles : undefined }]);
    setDraft('');
    setBusy(true);
    setLive({ text: '', steps: [] });
    const onProgress = (event: AskProgress) => setLive((current) => {
      if (event.type === 'turn') return { ...current, text: '' };
      if (event.type === 'text') return { ...current, text: current.text + (event.text ?? '') };
      if (event.type === 'heard') return { ...current, steps: [...current.steps, { tool: 'heard', input: { text: event.text } }] };
      return { ...current, steps: [...current.steps, { tool: event.tool ?? '', input: event.input ?? {} }] };
    });
    try {
      if (context) contextRef.current = context;
      if (!history.length) setThreadScreen({ screen: page.screen, name: page.name });
      const answer = await askGargiStream(text, history, contextRef.current ?? pageContext, onProgress, activeModel || undefined, attachments.map((f) => f.id), conversationRef.current);
      setTurns((t) => [...t, { role: 'assistant', content: answer.answer, answer }]);
      // Notes added after Gargi's last step become the next question.
      if (answer.unread?.length) setFollowUp(answer.unread.join(' '));
    } catch (error: any) {
      const status = error?.response?.status;
      const message = status === 403 ? 'Ask Gargi is available to CASA staff during the preview.'
        : status === 503 ? 'Gargi is not available right now. Try again in a few minutes.'
          : error?.code === 'ECONNABORTED' ? 'That took too long. Try a narrower question.'
            : 'Gargi could not answer just now. Try again in a moment.';
      if (status === 503) refetchStatus();
      setTurns((t) => [...t, { role: 'assistant', content: message, error: message }]);
    } finally {
      setBusy(false);
    }
  }, [busy, turns, refetchStatus, activeModel, attachments, pageContext, page.screen, page.name]);

  // Each finished answer saves the conversation to this person's history, so it can be reopened and continued.
  useEffect(() => {
    if (busy || !turns.length || turns[turns.length - 1].role !== 'assistant') return;
    saveConversation(conversationRef.current, turns)
      .then(() => queryClient.invalidateQueries({ queryKey: [HISTORY_KEY, selectedBusiness?.id] }))
      .catch(() => undefined);   // history is a convenience; the answer itself is already on screen
  }, [busy, turns, queryClient, selectedBusiness?.id]);

  const reopen = (id: string, saved: ChatTurn[]) => {
    setTurns(saved); setAttachments([]); setUploadError(''); contextRef.current = undefined; conversationRef.current = id;
    setPanel('chat'); setThreadScreen(null);
  };

  useEffect(() => {
    if (open && pending && pending.id !== lastPending.current) {
      // A question from a card or screen is put in the box for the person to read, edit and send with Enter;
      // what the card was about goes with it.
      lastPending.current = pending.id;
      if (pending.context) contextRef.current = pending.context;
      setPanel('chat');
      setDraft(pending.question);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open, pending]);

  // While Gargi works, Enter adds to the current question (read at its next step), like adding to a running task.
  const [followUp, setFollowUp] = useState<string | null>(null);
  useEffect(() => {
    if (followUp && !busy) { const text = followUp; setFollowUp(null); send(text); }
  }, [followUp, busy, send]);
  const submit = () => {
    const text = draft.trim();
    if (text.length < 2) return;
    if (!busy) { send(text); return; }
    setDraft('');
    setTurns((t) => [...t, { role: 'user', content: text, addedWhileWorking: true }]);
    interjectGargi(conversationRef.current, text).catch(() => setFollowUp(text));
  };

  const general = suggestions.data?.questions?.length ? suggestions.data.questions : FALLBACK_QUESTIONS;
  // This screen's questions first, then general ones.
  const questions = [...page.questions, ...general.filter((q) => !page.questions.includes(q))].slice(0, 5);
  const switchedScreen = !!threadScreen && threadScreen.screen !== page.screen && turns.length > 0 && !busy && panel === 'chat';
  // Known only once the suggestions load; until then questions can be asked and any failure is explained.
  const unavailable = suggestions.data?.analyst && !suggestions.data.analyst.ready ? suggestions.data.analyst : null;
  unavailableRef.current = !!unavailable;

  return (
    <Drawer anchor='right' open={open} onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', md: 600 }, display: 'flex' } }}>
      <Stack direction='row' alignItems='center' spacing={1.25} sx={{ px: 2.5, py: 1.75 }}>
        <Avatar sx={{ bgcolor: 'primary.main', width: 34, height: 34 }}><AutoAwesomeRoundedIcon fontSize='small' /></Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant='subtitle1' fontWeight={700} lineHeight={1.2}>Ask Gargi</Typography>
          <Typography variant='caption' color='text.secondary' noWrap>
            On {page.name} · {selectedBusiness?.name ?? 'this business'}
          </Typography>
        </Box>
        <Tooltip title={panel === 'memory' ? 'Back to the conversation' : 'What Gargi remembers'}>
          <span><IconButton size='small' onClick={() => setPanel((v) => (v === 'memory' ? 'chat' : 'memory'))} color={panel === 'memory' ? 'primary' : 'default'}>
            <PsychologyRoundedIcon fontSize='small' />
          </IconButton></span>
        </Tooltip>
        <Tooltip title={showHistory ? 'Back to the conversation' : 'Your conversations'}>
          <span><IconButton size='small' onClick={() => setPanel((v) => (v === 'history' ? 'chat' : 'history'))} disabled={busy} color={showHistory ? 'primary' : 'default'}>
            <HistoryRoundedIcon fontSize='small' />
          </IconButton></span>
        </Tooltip>
        {!!turns.length && (
          <Tooltip title='New conversation'>
            <span><IconButton size='small' onClick={restart} disabled={busy}><RestartAltRoundedIcon fontSize='small' /></IconButton></span>
          </Tooltip>
        )}
        <IconButton size='small' onClick={onClose}><CloseRoundedIcon fontSize='small' /></IconButton>
      </Stack>
      <Divider />

      <Box ref={scroller} onScroll={onScroll} sx={{ flex: 1, overflowY: 'auto', px: 2.5, py: 2 }}>
        {unavailable && (
          <Alert severity='warning' sx={{ mb: 2 }}>
            <strong>Ask Gargi isn&apos;t available yet.</strong> Every other Gargi tab works without it; questions will work once
            Gargi&apos;s service is connected.
          </Alert>
        )}
        {showHistory && <ChatHistory onOpen={reopen} currentId={conversationRef.current} />}
        {panel === 'memory' && <MemoryPanel />}
        {switchedScreen && (
          <Alert severity='info' sx={{ mb: 2 }} action={(
            <Stack direction='row' spacing={0.5}>
              <Button color='inherit' size='small' onClick={() => setThreadScreen({ screen: page.screen, name: page.name })}>Continue here</Button>
              <Button color='inherit' size='small' onClick={() => { restart(); setThreadScreen(null); }}>New chat</Button>
            </Stack>
          )}>
            This chat started on {threadScreen?.name}. You&apos;re now on {page.name}.
          </Alert>
        )}
        {panel === 'chat' && !turns.length && (
          <Box>
            <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
              Ask about whom to target, what they will buy, when to reach them, how stores and products are doing, or what a campaign
              could be worth. Gargi works from this business&apos;s own data and proposes plays you can turn into journeys.
            </Typography>
            <Stack spacing={1}>
              {questions.map((q) => (
                <Paper key={q} variant='outlined' onClick={() => !unavailable && send(q)}
                  sx={{ px: 1.75, py: 1.25, borderRadius: 2, cursor: unavailable ? 'default' : 'pointer', opacity: unavailable ? 0.6 : 1,
                    '&:hover': unavailable ? {} : { borderColor: 'primary.main', bgcolor: 'primary.light' } }}>
                  <Typography variant='body2'>{q}</Typography>
                </Paper>
              ))}
            </Stack>
          </Box>
        )}
        <Stack spacing={2.5} sx={{ display: panel === 'chat' ? undefined : 'none' }}>
          {turns.map((turn, i) => (turn.role === 'user' ? (
            <Box key={i} sx={{ alignSelf: 'flex-end', maxWidth: '85%', px: 1.75, py: 1.25, borderRadius: 2.5, bgcolor: 'primary.main', color: 'primary.contrastText' }}>
              <Typography variant='body2'>{turn.content}</Typography>
              {turn.addedWhileWorking && <Typography variant='caption' sx={{ opacity: 0.8 }}>Added while Gargi was working</Typography>}
              {!!turn.files?.length && (
                <Stack direction='row' spacing={0.5} flexWrap='wrap' useFlexGap sx={{ mt: 0.75 }}>
                  {turn.files.map((f) => (
                    <Chip key={f.id} size='small' icon={<InsertDriveFileOutlinedIcon />} label={f.name}
                      sx={{ bgcolor: 'rgba(255,255,255,0.18)', color: 'inherit', '& .MuiChip-icon': { color: 'inherit' } }} />
                  ))}
                </Stack>
              )}
            </Box>
          ) : (
            <Box key={i}>
              {turn.error ? <Alert severity='warning'>{turn.error}</Alert> : turn.answer && <AnswerView answer={turn.answer} onAsk={busy ? undefined : send} question={turns[i - 1]?.role === 'user' ? turns[i - 1].content : undefined} />}
            </Box>
          )))}
          {busy && <Thinking live={live} />}
        </Stack>
        <div ref={bottom} />
      </Box>

      <Divider />
      <Box sx={{ p: 2 }}>
        {(!!attachments.length || uploading || uploadError) && (
          <Stack spacing={0.75} sx={{ mb: 1 }}>
            {!!attachments.length && (
              <Stack direction='row' spacing={0.75} flexWrap='wrap' useFlexGap>
                {attachments.map((f) => (
                  <Chip key={f.id} size='small' variant='outlined' icon={<InsertDriveFileOutlinedIcon />} label={`${f.name} · ${fileSize(f.size)}`}
                    onDelete={busy ? undefined : () => setAttachments((current) => current.filter((x) => x.id !== f.id))} />
                ))}
              </Stack>
            )}
            {uploading && <LinearProgress sx={{ borderRadius: 1 }} />}
            {uploadError && <Alert severity='warning' onClose={() => setUploadError('')}>{uploadError}</Alert>}
          </Stack>
        )}
        <input ref={fileInput} type='file' hidden multiple accept={ACCEPT} onChange={(e) => attach(e.target.files)} />
        <Stack direction='row' spacing={1} alignItems='flex-end'>
          <Tooltip title='Attach a file to analyse (CSV, Excel, PDF, image — up to 20 MB)'>
            <span>
              <IconButton onClick={() => fileInput.current?.click()} disabled={busy || uploading || !!unavailable} aria-label='Attach a file'>
                <AttachFileRoundedIcon />
              </IconButton>
            </span>
          </Tooltip>
          <TextField
            fullWidth multiline maxRows={5} size='small' placeholder={unavailable ? 'Ask Gargi is not connected yet' : busy ? 'Add to the question while Gargi works…' : 'Ask a question…'}
            value={draft} disabled={!!unavailable} inputRef={inputRef}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(); } }}
            inputProps={{ maxLength: 2000 }}
          />
          <IconButton color='primary' onClick={submit} disabled={!!unavailable || draft.trim().length < 2}
            sx={{ bgcolor: 'primary.light', '&:hover': { bgcolor: 'primary.light' } }}>
            <SendRoundedIcon />
          </IconButton>
        </Stack>
        <Stack direction='row' spacing={1} alignItems='center' justifyContent='space-between' sx={{ mt: 0.75 }}>
          <Typography variant='caption' color='text.secondary'>
            Gargi answers from read-only analyses of this business. Check the numbers before acting.
          </Typography>
          {!!catalog?.models.length && (
            <Box sx={{ flexShrink: 0 }}>
              <JourneyModelPicker models={catalog.models} value={activeModel} onChange={pickModel} disabled={busy} />
            </Box>
          )}
        </Stack>
      </Box>
    </Drawer>
  );
};

// Ask Gargi for the whole app: the drawer, the context every "Ask Gargi" button opens it with, and (floating) a
// button on every page. Only for people with Gargi access; without it, components see the default (unavailable)
// context and offer nothing. The page the person is on goes with their questions unless a card gave a context.
const OWN_GARGI = [/^\/journeys\/[^/]+\/edit/, /^\/journeys\/from-assignment\//];

export const GargiProvider: FC<{ children: ReactNode; floating?: boolean }> = ({ children, floating = false }) => {
  const allowed = useGargiAi();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState<DrawerProps['pending']>(null);
  const value = useMemo<GargiContextValue>(() => ({
    open: (question, context) => {
      if (question) setPending({ question, context, id: Date.now() });
      setOpen(true);
    },
    available: true,
  }), []);
  if (!allowed) return <>{children}</>;
  const showButton = floating && !open && !OWN_GARGI.some((pattern) => pattern.test(location.pathname));
  return (
    <GargiContext.Provider value={value}>
      {children}
      <AskGargiDrawer open={open} onClose={() => setOpen(false)} pending={pending}
        page={{ ...gargiPage(location.pathname, document.title), path: location.pathname }} />
      {showButton && (
        // A round button in the corner, so it doesn't cover the right edge of tables; the label shows on hover.
        <Tooltip title='Ask Gargi' placement='left'>
          <Fab color='primary' size='medium' aria-label='Ask Gargi' onClick={() => setOpen(true)}
            sx={{ position: 'fixed', right: 20, bottom: 20, zIndex: (theme) => theme.zIndex.drawer - 1 }}>
            <AutoAwesomeRoundedIcon />
          </Fab>
        </Tooltip>
      )}
    </GargiContext.Provider>
  );
};
